// ==========================
// FILE: src/components/Videos.js
// ==========================

import React, { useState, useRef, useEffect, useMemo } from "react";
import { db, storage } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import PageScaffold from "./ui/PageScaffold";
import Modal from "./ui/Modal";
import VideoLightbox from "./ui/VideoLightbox";
import { useAccess } from "../access/AccessContext";
import {
  openDownloadForAttachment,
  toStoragePath,
  buildForwardMailto,
  downloadTextFile,
} from "../utils/managerActions";
import "../styles/layout.css";

/* ---------------- Helpers (kept minimal) ---------------- */
const isHttp = (u) => typeof u === "string" && /^https?:\/\//i.test(u);

function toStoragePathLoose(maybe) {
  if (!maybe || typeof maybe !== "string") return null;
  if (maybe.startsWith("gs://")) {
    const no = maybe.replace(/^gs:\/\//, "");
    const slash = no.indexOf("/");
    return slash >= 0 ? no.slice(slash + 1) : null;
  }
  if (maybe.startsWith("http")) {
    const m = maybe.match(/\/o\/([^?]+)/);
    if (!m) return null;
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }
  if (maybe.includes("..")) return null;
  return maybe;
}
async function toHttpsUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (isHttp(pathOrUrl)) return pathOrUrl;
  try {
    const clean = toStoragePathLoose(pathOrUrl) || pathOrUrl;
    const ref = storageRef(storage, clean);
    return await getDownloadURL(ref);
  } catch {
    return null;
  }
}
const extFromPath = (p = "") => (String(p).toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] || "");
const extOf = (u = "") => (String(u).toLowerCase().match(/\.([a-z0-9]+)(?:$|\?)/)?.[1] || "");

async function writeBlobToDir(dirHandle, filename, blob) {
  const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}
async function writeTextToDir(dirHandle, filename, text) {
  const blob = new Blob([text || ""], { type: "text/plain;charset=utf-8" });
  return writeBlobToDir(dirHandle, filename, blob);
}

const VIDEO_SUPPORT = (() => {
  if (typeof document === "undefined") return { mp4: true, webm: true };
  const v = document.createElement("video");
  const can = (type) => (v && v.canPlayType ? v.canPlayType(type) !== "" : true);
  return {
    mp4: can('video/mp4; codecs="avc1.42E01E, mp4a.40.2"') || can('video/mp4'),
    webm: can('video/webm; codecs="vp9, opus"') || can('video/webm'),
  };
})();
function isPlayableVideoEXT(ext) {
  const ex = (ext || "").toLowerCase();
  if (["mp4", "m4v", "mov"].includes(ex)) return VIDEO_SUPPORT.mp4;
  if (ex === "webm") return VIDEO_SUPPORT.webm;
  return true; // best effort
}

// Convert entry to media fields
function pickMedia(entry) {
  return {
    mediaUrl: entry?.mediaUrl || "",
    storagePath: entry?.storagePath || null,
    convertedPath: entry?.convertedPath || null,
    thumbPath: entry?.thumbPath || null,
    convertProgress: typeof entry?.convertProgress === 'number' ? entry.convertProgress : null,
  };
}
function useResolvedMedia(entry) {
  const [src, setSrc] = useState(null);
  const [poster, setPoster] = useState(null);
  const dep = useMemo(() => ({
    u: entry?.mediaUrl,
    cp: entry?.convertedPath,
    tp: entry?.thumbPath,
  }), [entry?.mediaUrl, entry?.convertedPath, entry?.thumbPath]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { mediaUrl, convertedPath, thumbPath } = pickMedia(entry || {});
      const play = convertedPath || mediaUrl || null;
      const post = thumbPath || null;
      const [sUrl, pUrl] = await Promise.all([ toHttpsUrl(play), toHttpsUrl(post) ]);
      if (!alive) return;
      setSrc(sUrl || null);
      setPoster(pUrl || null);
    })();
    return () => { alive = false; };
    // ✅ include `entry` to satisfy exhaustive-deps
  }, [dep, entry]);
  return { src, poster };
}
function pickRecorder() {
  const sup = (m) => (window.MediaRecorder?.isTypeSupported?.(m) ? true : false);
  const candidates = [
    'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
    'video/mp4',
    'video/webm;codecs="vp9,opus"',
    'video/webm',
  ];
  const mime = candidates.find(sup) || "";
  const ext = mime.startsWith("video/mp4") ? "mp4" : mime.includes("webm") ? "webm" : "webm";
  return { mime: mime || undefined, ext };
}
function canRecordVideo() {
  if (!navigator?.mediaDevices?.getUserMedia || !window.MediaRecorder) return false;
  const { mime } = pickRecorder();
  return !!mime;
}

/* ---------------- Component ---------------- */
export default function Videos({ user }) {
  const { readOnly, ownerUid: managedOwnerUid } = useAccess() || { readOnly: false, ownerUid: null };

  const [videos, setVideos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [eta, setEta] = useState("");
  const [editVideo, setEditVideo] = useState(null);
  const [error, setError] = useState("");

  const [recording, setRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const liveRef = useRef();

  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // View modal for read-only
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const fileInput = useRef();

  // live list
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "videos"),
      where("uid", "==", user.uid),
      orderBy("created", "desc")
    );
    return onSnapshot(q, (snap) =>
      setVideos(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, [user?.uid]);

  // revoke object URL on unmount/change
  useEffect(() => () => { try { if (previewUrl) URL.revokeObjectURL(previewUrl); } catch {} }, [previewUrl]);

  // live camera preview
  useEffect(() => {
    const el = liveRef.current;
    if (el && recording && mediaStream) {
      el.srcObject = mediaStream;
    }
    return () => {
      if (el) {
        el.pause?.();
        el.srcObject = null;
      }
    };
  }, [recording, mediaStream]);

  function openForm(video = null) {
    if (!video && free && videos.length >= 1) { setShowUpgrade(true); return; }
    setShowForm(true);
    setEditVideo(video);
    setNote(video?.note || "");
    try { if (previewUrl) URL.revokeObjectURL(previewUrl); } catch {}
    setPreviewUrl(null);
    setFile(null);
    setError("");
    setUploadPct(0);
    setEta("");
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  }
  function closeForm() {
    setShowForm(false);
    setEditVideo(null);
    setNote("");
    try { if (previewUrl) URL.revokeObjectURL(previewUrl); } catch {}
    setPreviewUrl(null);
    setFile(null);
    setError("");
    setUploadPct(0);
    setEta("");
    setUploading(false);
  }
  function handleFile(e) {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      try { setPreviewUrl(URL.createObjectURL(f)); } catch {}
      setError("");
    }
  }

  async function startRecording() {
    try {
      const { mime, ext } = pickRecorder();
      if (!mime) throw new Error("Recording not supported on this browser/device");
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setMediaStream(stream);
      const mr = new MediaRecorder(stream, { mimeType: mime });
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mime || "video/webm" });
        const newFile = new File([blob], `video.${ext}`, { type: blob.type });
        setFile(newFile);
        try { setPreviewUrl(URL.createObjectURL(blob)); } catch {}
        stopRecording();
      };
      mr.start();
      setMediaRecorder(mr);
      setRecording(true);
    } catch (err) {
      alert("Recording error: " + (err?.message || String(err)));
    }
  }
  function stopRecording() {
    try { mediaRecorder?.stop(); } catch {}
    try { mediaStream?.getTracks().forEach((t) => t.stop()); } catch {}
    setRecording(false);
    setMediaStream(null);
    setMediaRecorder(null);
  }

  async function deleteCurrentMedia() {
    if (uploading) return;
    try { stopRecording(); } catch {}
    if (file || previewUrl) {
      try { if (previewUrl) URL.revokeObjectURL(previewUrl); } catch {}
      if (fileInput.current) fileInput.current.value = "";
      setFile(null);
      setPreviewUrl(null);
      setError("Removed. Choose another file to replace.");
      return;
    }
    if (editVideo && (editVideo.storagePath || editVideo.mediaUrl || editVideo.convertedPath || editVideo.thumbPath)) {
      if (!window.confirm("Remove the current video from this entry? You can upload a replacement next.")) return;
      try {
        if (editVideo.storagePath) {
          await deleteObject(storageRef(storage, editVideo.storagePath)).catch(() => {});
        } else if (editVideo.mediaUrl) {
          const enc = editVideo.mediaUrl.split("/o/")[1]?.split("?")[0] || "";
          if (enc) await deleteObject(storageRef(storage, decodeURIComponent(enc))).catch(() => {});
        }
        if (editVideo.convertedPath) await deleteObject(storageRef(storage, editVideo.convertedPath)).catch(() => {});
        if (editVideo.thumbPath) await deleteObject(storageRef(storage, editVideo.thumbPath)).catch(() => {});
      } catch {}
      try {
        await updateDoc(doc(db, "videos", editVideo.id), {
          mediaUrl: null,
          storagePath: null,
          convertedPath: null,
          thumbPath: null,
          updated: serverTimestamp(),
        });
      } catch {}
      setEditVideo((prev) => prev ? ({ ...prev, mediaUrl: "", storagePath: null, convertedPath: null, thumbPath: null }) : prev);
      setError("Removed. Choose another file to replace.");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!user?.uid) return;

    if (!file && !editVideo?.mediaUrl && !editVideo?.convertedPath) {
      setError("Please choose a video.");
      return;
    }

    setUploading(true);
    setUploadPct(0);
    setEta("");

    try {
      let docId = editVideo?.id || null;
      let storagePath = editVideo?.storagePath || null;
      let mediaUrl = editVideo?.mediaUrl || "";
      let convertedPath = editVideo?.convertedPath || null;
      let thumbPath = editVideo?.thumbPath || null;

      let plannedPath = storagePath;
      if (file) {
        const safeName = (file.name || `upload_${Date.now()}.mp4`).replace(/[^A-Za-z0-9_.-]/g, "_");
        plannedPath = `videos/${user.uid}/${user.uid}_${Date.now()}_${safeName}`;
      }

      if (!docId) {
        const base = {
          uid: user.uid,
          note,
          mediaUrl: mediaUrl || null,
          storagePath: plannedPath || null,
          convertedPath: convertedPath || null,
          thumbPath: thumbPath || null,
          created: serverTimestamp(),
          updated: serverTimestamp(),
        };
        const dref = await addDoc(collection(db, "videos"), base);
        docId = dref.id;
      } else if (plannedPath && plannedPath !== storagePath) {
        await updateDoc(doc(db, "videos", docId), { storagePath: plannedPath, updated: serverTimestamp() });
      }

      if (file) {
        const sref = storageRef(storage, plannedPath);
        const task = uploadBytesResumable(sref, file);
        const startTs = Date.now();
        await new Promise((resolve, reject) => {
          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
              setUploadPct(pct);
              const dt = (Date.now() - startTs) / 1000;
              const speed = snap.bytesTransferred / Math.max(1, dt);
              const remaining = snap.totalBytes - snap.bytesTransferred;
              const etaSec = remaining / Math.max(1, speed);
              setEta(etaSec > 0 ? `${Math.round(etaSec)}s` : "");
            },
            reject,
            resolve
          );
        });
        mediaUrl = await getDownloadURL(task.snapshot.ref);
        storagePath = task.snapshot.ref.fullPath;
        convertedPath = null;
        thumbPath = null;
        await updateDoc(doc(db, "videos", docId), {
          note,
          mediaUrl,
          storagePath,
          convertedPath,
          thumbPath,
          updated: serverTimestamp(),
        });
      } else {
        await updateDoc(doc(db, "videos", docId), { note, updated: serverTimestamp() });
      }

      closeForm();
    } catch (err) {
      setError("Save failed: " + (err?.message || String(err)));
      setUploading(false);
    }
  }

  async function handleDelete(video) {
    if (!window.confirm("Delete this video?")) return;
    try {
      if (video.storagePath) {
        await deleteObject(storageRef(storage, video.storagePath)).catch(() => {});
      } else if (video.mediaUrl) {
        const enc = video.mediaUrl.split("/o/")[1]?.split("?")[0] || "";
        if (enc) await deleteObject(storageRef(storage, decodeURIComponent(enc))).catch(() => {});
      }
      if (video.convertedPath) await deleteObject(storageRef(storage, video.convertedPath)).catch(() => {});
      if (video.thumbPath) await deleteObject(storageRef(storage, video.thumbPath)).catch(() => {});
      await deleteDoc(doc(db, "videos", video.id));
      if (editVideo?.id === video.id) closeForm();
    } catch (err) {
      alert("Delete failed: " + (err?.message || String(err)));
    }
  }

  // Header actions
  const actionsDesktop = (
    <>
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideoModal(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="dp-help-row">
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideoModal(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );

  // For edit modal preview + poster fallback
  const { src: editSrc, poster: editPosterNet } = useResolvedMedia(editVideo || {});
  const [editPosterGen, setEditPosterGen] = useState(null);
  useEffect(() => {
    const source = previewUrl || editSrc || "";
    if (!source) { setEditPosterGen(null); return; }
    let v; let alive = true;
    try {
      v = document.createElement('video');
      v.preload = 'metadata'; v.muted = true; v.playsInline = true; v.src = source;
      v.onloadedmetadata = () => { try { v.currentTime = Math.min(0.3, (v.duration || 1) * 0.05); } catch {} };
      v.onseeked = () => {
        if (!alive) return;
        try {
          const c = document.createElement('canvas');
          c.width = v.videoWidth || 320; c.height = v.videoHeight || 180;
          c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
          setEditPosterGen(c.toDataURL('image/jpeg', 0.75));
        } catch {}
      };
    } catch {}
    return () => { alive = false; try { if (v) v.src = ""; } catch {} };
  }, [previewUrl, editSrc]);

  function VideoRow({ v }) {
    const { src, poster } = useResolvedMedia(v);
    const isConverted = !!v.convertedPath;
    const ex = extFromPath(v.storagePath) || extOf(v.mediaUrl || "");
    const playable = isConverted ? true : isPlayableVideoEXT(ex);
    const processing = !isConverted && !!v.mediaUrl && !["mp4","m4v","mov","webm"].includes((ex||"").toLowerCase());

    const [reveal, setReveal] = useState(false);
    useEffect(() => { setReveal(false); }, [v?.id, src]);

    const created = v.created?.toDate ? v.created.toDate() : (v.updated?.toDate ? v.updated.toDate() : null);
    const canOpen = !!src && playable && !processing;

    const ownerAction = (
      <button className="btn btn-main btn-edit" onClick={() => openForm(v)}>
        Edit
      </button>
    );
    const managerAction = (
      <button
        className="btn btn-main btn-edit"
        onClick={() => { setViewItem(v); setViewOpen(true); }}
        data-allow-readonly
        title="View"
      >
        View
      </button>
    );

    return (
      <div className="card dv-item">
        <div className="dv-left">
          <div
            className={`thumb-wrap ${canOpen ? 'is-clickable' : ''}`}
            onClick={() => canOpen && setReveal(r => !r)}
            onKeyDown={(e) => { if (canOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setReveal(r => !r); }}}
            role={canOpen ? 'button' : undefined}
            tabIndex={canOpen ? 0 : -1}
            aria-label={canOpen ? (reveal ? 'Hide inline player' : 'Play inline') : (processing ? 'Processing' : 'Not playable yet')}
            title={canOpen ? (reveal ? 'Hide player' : 'Play inline') : (processing ? 'Processing…' : 'Not playable on this device')}
          >
            {poster ? (
              <img src={poster} alt="thumb" className="tile-thumb" />
            ) : (
              <div className="tile-thumb" style={{display:"grid",placeItems:"center",background:"#f3f3f3"}}>🎬</div>
            )}
            {canOpen ? (
              <span className="thumb-play" aria-hidden="true">
                {reveal ? (
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M8 5v14l11-7z"></path></svg>
                )}
              </span>
            ) : (
              <span className="thumb-badge" aria-hidden="true">{processing ? "Processing…" : "Incompatible"}</span>
            )}
          </div>
        </div>
        <div className="dv-main">
          <div className="tile-title">{v.note || "Video"}</div>
          <div className="tile-sub">{created ? created.toLocaleString() : ""}</div>
          {processing && (
            <div className="processing">Processing… we’ll switch to the converted video when ready.</div>
          )}
        </div>
        <div className="dv-actions">
          {readOnly ? managerAction : ownerAction}
        </div>

        {reveal && src && canOpen && (
          <div className="dv-inline-player">
            <video src={src} poster={poster || undefined} controls autoPlay playsInline style={{ width: '100%', borderRadius: 8 }} />
            <div className="dv-inline-tools">
              <button type="button" className="btn btn-cancel btn-sm" onClick={() => setReveal(false)}>Close</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <PageScaffold
        title="Videos"
        onAdd={!readOnly ? () => openForm() : undefined}
        heroSrc="/lastwish-account-videos.jpg"
        heroAlt="Videos"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
        
      >
        <div className="dv-list">
          {videos.length === 0 ? (
            <div className="empty-state">No videos yet.</div>
          ) : (
            videos.map((v) => <VideoRow key={v.id} v={v} />)
          )}
        </div>
      </PageScaffold>

      {/* Edit/Add modal (owner only) */}
      <Modal open={!readOnly && showForm} title={editVideo ? "Edit Video" : "New Video"} onClose={closeForm}>
        <form onSubmit={handleSubmit}>
          <textarea
            className="input"
            placeholder="Add a note…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
          />

          {!uploading && !recording && (
            <button
              type="button"
              className="btn btn-main"
              onClick={startRecording}
              disabled={!canRecordVideo()}
              title={!canRecordVideo() ? 'Recording not supported in this browser/device' : undefined}
              style={{ marginBottom: 8 }}
            >
              🎥 Record Video
            </button>
          )}
          {recording && (
            <div style={{ margin: '8px 0' }}>
              <video ref={liveRef} autoPlay playsInline muted style={{ width: 220, borderRadius: 6 }} />
              <div style={{ marginTop: 6 }}>
                <button type="button" className="btn btn-cancel" onClick={stopRecording}>Stop Recording</button>
              </div>
            </div>
          )}

          <input
            className="input"
            type="file"
            accept="video/*"
            ref={fileInput}
            onChange={handleFile}
          />

          {(previewUrl || editSrc) && !recording && (
            (() => {
              const ext = previewUrl && file ? extOf(file.name) : (extFromPath(editVideo?.storagePath) || extOf(editVideo?.mediaUrl || ""));
              const playable = !!editVideo?.convertedPath || isPlayableVideoEXT(ext);
              return playable ? (
                <div className="dv-preview-wrap">
                  <video
                    src={previewUrl || editSrc}
                    poster={editPosterNet || editPosterGen || undefined}
                    controls
                    playsInline
                    preload="metadata"
                    style={{ width: "100%", borderRadius: 8, marginBottom: 8 }}
                  />
                  <button type="button" className="dv-delete" onClick={deleteCurrentMedia} title="Remove this video">×</button>
                </div>
              ) : (
                <div className="processing" style={{marginBottom:8}}>Processing… we’ll switch to the converted video when ready.</div>
              );
            })()
          )}

          {uploading && (
            <div className="progress">
              <div className="bar" style={{ width: `${uploadPct}%` }} />
              <div className="pct">{uploadPct}% {eta && `• ${eta} left`}</div>
            </div>
          )}

          {error && <p style={{ color: "#d9534f" }}>{error}</p>}

          <div className="form-actions">
            <button className="btn btn-main" disabled={uploading}>{editVideo ? "Update" : "Save"}</button>
            {editVideo && (
              <button type="button" className="btn btn-danger" onClick={() => handleDelete(editVideo)} disabled={uploading}>Delete</button>
            )}
            <button type="button" className="btn btn-cancel" onClick={closeForm}>Cancel</button>
          </div>
        </form>
      </Modal>

      {/* Read-only VIEW modal (managers) */}
      {readOnly && viewOpen && viewItem && (
        <div className="modal-bg" onClick={() => { setViewOpen(false); setViewItem(null); }}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <h3>{viewItem.note || "Video"}</h3>
            <div className="modal-body">
              <ResolvedVideo item={viewItem} />
              <div className="form-actions" style={{ marginTop: 10 }}>
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={async () => {
                    const owner = managedOwnerUid || user?.uid;
                    if (!owner) return;

                    const note = (viewItem?.note || "Video").trim();
                    const safeBase = (note || "video").replace(/[^\w.-]+/g, "_").slice(0, 80) || "video";

                    const createdStr = viewItem?.created?.toDate ? viewItem.created.toDate().toLocaleString() : "";
                    const updatedStr = viewItem?.updated?.toDate ? viewItem.updated.toDate().toLocaleString() : "";

                    const textContent =
`Title: ${note || "Video"}
Created: ${createdStr}
Updated: ${updatedStr}

Note:
${viewItem?.note || ""}

`;
                    const textFilename = `${safeBase}.txt`;

                    // Prefer direct URL when present (works for owners/managers). Fall back to storage path.
                    const fallbackPath =
                      viewItem?.convertedPath ||
                      viewItem?.storagePath ||
                      toStoragePath(viewItem?.mediaUrl) ||
                      toStoragePathLoose(viewItem?.mediaUrl) ||
                      null;

                    const directUrl = isHttp(viewItem?.mediaUrl) ? viewItem.mediaUrl : null;
                    const videoUrl = await toHttpsUrl(directUrl || fallbackPath);

                    const ext = extOf(videoUrl) || extFromPath(fallbackPath) || "mp4";
                    const videoFilename = `${safeBase}.${ext}`;

                    // If the browser supports folder picking (Chrome/Edge), save both files into a chosen folder.
                    if (typeof window !== "undefined" && window.showDirectoryPicker) {
                      try {
                        const root = await window.showDirectoryPicker();
                        const folder = await root.getDirectoryHandle(`LastWishBox_${safeBase}`, { create: true });

                        await writeTextToDir(folder, textFilename, textContent);

                        if (videoUrl) {
                          const res = await fetch(videoUrl);
                          if (res.ok) {
                            const blob = await res.blob();
                            await writeBlobToDir(folder, videoFilename, blob);
                            return;
                          }
                        }

                        // If we couldn't fetch the video blob, fall back to normal download for the video.
                        downloadTextFile(textFilename, textContent);
                        const ok = await openDownloadForAttachment({
                          ownerUid: owner,
                          pathOrUrl: directUrl || fallbackPath,
                          fallbackPathOrUrl: fallbackPath,
                          suggestedName: videoFilename,
                        });
                        if (!ok) alert("Could not generate a download link.");
                        return;
                      } catch {
                        // Folder pick cancelled or not permitted; fall back below.
                      }
                    }

                    // Default fallback: download text, then download video as a separate file.
                    downloadTextFile(textFilename, textContent);
                    const ok = await openDownloadForAttachment({
                      ownerUid: owner,
                      pathOrUrl: directUrl || fallbackPath,
                      fallbackPathOrUrl: fallbackPath,
                      suggestedName: videoFilename,
                    });
                    if (!ok) alert("Could not generate a download link.");
                  }}
                  data-allow-readonly
                >
                  Download
                </button>
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={() => window.print()}
                  data-allow-readonly
                >
                  Print
                </button>
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={async () => {
                    const owner = managedOwnerUid || user?.uid;
                    const mailto = await buildForwardMailto({
                      ownerUid: owner,
                      pathOrUrl: viewItem?.convertedPath || viewItem?.mediaUrl,
                      subject: `Video${viewItem?.note ? `: ${viewItem.note}` : ""}`,
                      fields: {
                        Note: viewItem?.note || "-",
                        Created: viewItem?.created?.toDate ? viewItem.created.toDate().toLocaleString() : "",
                      },
                    });
                    window.location.href = mailto;
                  }}
                  data-allow-readonly
                >
                  Forward
                </button>
                <button
                  className="btn btn-cancel"
                  type="button"
                  onClick={() => { setViewOpen(false); setViewItem(null); }}
                  data-allow-readonly
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video modal — unchanged */}
      <VideoLightbox
        open={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        url="https://youtu.be/eugXQfrP1zI?si=KnnB1HqpdMScVK2D"
        title="How the Videos page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* DP right drawer (unchanged) */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Videos page works</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>Print</button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly>Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p>Upload a clip and add a note. Some uploads (e.g., WEBM) may show as <i>Processing</i> until the MP4 is ready.</p>
              <ol>
                <li>Click <b>+</b> to add a new video.</li>
                <li>Select a file or <b>Record Video</b>, then Save. You’ll see progress while uploading.</li>
                <li>The list updates live; inline playback appears once compatible.</li>
              </ol>
            </div>
          </aside>
        </div>
      )}

      <style>{`
        .dv-list{display:grid;grid-template-columns:1fr;gap:16px}
        .dv-item{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:12px;padding:12px}
        .dv-left{width:42px;position:relative}
        .thumb-wrap{position:relative;width:42px;height:42px}
        .thumb-wrap.is-clickable{cursor:pointer}
        .tile-thumb{width:42px;height:42px;border-radius:8px;object-fit:cover;background:#000}
        .thumb-play{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:22px;height:22px;border-radius:999px;background:rgba(0,0,0,.45);color:#fff;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 3px rgba(0,0,0,.25);pointer-events:none}
        .thumb-badge{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);background:rgba(0,0,0,.65);color:#fff;font-size:10px;padding:3px 6px;border-radius:999px;pointer-events:none;white-space:nowrap}
        .dv-inline-player{grid-column:1 / -1;margin-top:8px}
        .dv-inline-tools{display:flex;justify-content:flex-end;margin-top:6px}
        .btn.btn-sm{padding:6px 10px;font-size:12px;border-radius:6px}
        .processing{margin-top:6px;font-size:12px;color:#6b7280}

        .progress { position: relative; height: 10px; background: #eee; border-radius: 999px; margin: 8px 0 10px; }
        .progress .bar { position: absolute; left:0; top:0; bottom:0; border-radius: 999px; background: #4f46e5; transition: width .15s linear; }
        .progress .pct { text-align:right; font-size:12px; color:#6b7280; margin-top:4px; }

        :root { --topbar-h: 56px; --orange:#f15822; --blue:#8cade1; }
        .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .dp-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .dp-help.read { background: #650202; color: #fff; }
        .dp-help.see  { background: #003353; color: #fff; }
        .dp-help-ico { font-size: 1.05rem; }
        .dp-intro { margin: 4px 0 10px; color: #2a0516; }
        .dp-video { position: relative; width: 100%; padding-top: 56.25%; border-radius: 10px; overflow: hidden; background: #000; margin-bottom: 12px; }
        .dp-video iframe { position: absolute; inset: 0; width: 100%; height: 100%; }
        .dp-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
        .dp-drawer { position: fixed; top: var(--topbar-h, 56px); right: 0; height: calc(100vh - var(--topbar-h, 56px)); width: min(520px, 95vw); background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15); border-top-left-radius: 12px; border-bottom-left-radius: 12px; display: flex; flex-direction: column; overflow: hidden; }
        .dp-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title { font-weight:700; color:#2a0516; }
        .dp-drawer-tools { display:flex; gap:8px; }
        .dp-drawer-body { padding:16px; overflow:auto; }

        .dv-preview-wrap{ position:relative; }
        .dv-preview-wrap video{ display:block; }
        .dv-delete{ position:absolute; top:8px; right:8px; width:28px; height:28px; border-radius:999px; background:var(--orange,#f15822); color:#fff; border:none; display:flex; align-items:center; justify-content:center; font-size:18px; line-height:1; box-shadow:0 1px 3px rgba(0,0,0,.25); cursor:pointer; z-index:2; }
        .dv-delete:hover{ filter:brightness(0.95); }
        .dv-delete:focus{ outline:2px solid rgba(0,0,0,0.15); outline-offset:2px; }
    `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="documents" />
    </>
  );
}

/* Tiny child renderer so we can use the hook correctly in the View modal */
function ResolvedVideo({ item }) {
  const { src, poster } = useResolvedMedia(item || {});
  if (!src) return <div className="empty-state">No preview available.</div>;
  return (
    <video
      src={src}
      poster={poster || undefined}
      controls
      playsInline
      preload="metadata"
      style={{ width: "100%", borderRadius: 8 }}
    />
  );
}
